import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';

export interface EmptyStateCta {
  label: string;
  /** Internal react-router target. Mutually exclusive with `onClick` /
   *  `href`. If multiple are provided, `to` wins. */
  to?: string;
  /** External href. */
  href?: string;
  /** Imperative action (clear filters, open a dialog, etc.). */
  onClick?: () => void;
  /** Visual prominence — primary is filled, secondary is outlined. */
  variant?: 'primary' | 'secondary';
}

export interface EmptyStateWithRedirectProps {
  /** Icon rendered above the title. Pass a sized lucide icon (e.g.
   *  `<Activity className="w-8 h-8" />`). */
  icon?: React.ReactNode;
  /** Short headline (one line). */
  title: string;
  /** Optional secondary line — explains the empty condition. */
  subtitle?: string;
  /** Primary call-to-action. Usually a route to the page where the
   *  user can fix the empty condition. */
  cta?: EmptyStateCta;
  /** Optional second CTA for when two paths make sense (e.g. "Create
   *  basket" / "Browse marketplace"). */
  secondaryCta?: EmptyStateCta;
  /** Vertical padding preset:
   *   - `'page'` (default): py-12, suits full-page empty states
   *   - `'card'`: py-6, suits Dashboard card / hero banner contexts
   */
  size?: 'page' | 'card';
  className?: string;
}

const PRIMARY_BTN_CLS =
  'inline-flex items-center justify-center gap-1.5 px-3 h-9 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors';
const SECONDARY_BTN_CLS =
  'inline-flex items-center justify-center gap-1.5 px-3 h-9 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors';

function ctaClass(cta: EmptyStateCta): string {
  return cta.variant === 'secondary' ? SECONDARY_BTN_CLS : PRIMARY_BTN_CLS;
}

function CtaElement({ cta }: { cta: EmptyStateCta }) {
  const cls = ctaClass(cta);
  if (cta.to) {
    return <Link to={cta.to} className={cls}>{cta.label}</Link>;
  }
  if (cta.href) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
      >
        {cta.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={cta.onClick} className={cls}>
      {cta.label}
    </button>
  );
}

/**
 * Centered empty-state with icon + title + optional subtitle + up to
 * two CTAs. Replaces ad-hoc "No X" one-liners scattered across the
 * frontend so empty surfaces consistently point users to the next
 * action — usually a route via react-router `<Link>`.
 *
 * Pair with helpers in `frontend/src/utils/emptyStates.ts` for
 * context-aware preset selection (e.g. Schedule view picks a
 * different CTA depending on whether the date is today vs historical).
 *
 * Size variants:
 *   - `'page'` (default): full-page empty state, py-12 padding,
 *     centered icon at w-8/h-8 typical.
 *   - `'card'`: embedded inside Dashboard cards / hero banner,
 *     py-6 padding, smaller visual footprint.
 */
const EmptyStateWithRedirect: React.FC<EmptyStateWithRedirectProps> = ({
  icon, title, subtitle, cta, secondaryCta, size = 'page', className,
}) => (
  <div
    role="status"
    aria-label={title}
    className={cn(
      'flex flex-col items-center justify-center text-center px-6',
      size === 'page' ? 'py-12' : 'py-6',
      className,
    )}
  >
    {icon && (
      <div className="mb-3 text-gray-300 dark:text-gray-600" aria-hidden="true">
        {icon}
      </div>
    )}
    <h3 className={cn(
      'font-semibold text-gray-900 dark:text-white',
      size === 'page' ? 'text-base' : 'text-sm',
    )}>
      {title}
    </h3>
    {subtitle && (
      <p className={cn(
        'mt-1 text-gray-500 dark:text-gray-400 max-w-md',
        size === 'page' ? 'text-sm' : 'text-xs',
      )}>
        {subtitle}
      </p>
    )}
    {(cta || secondaryCta) && (
      <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
        {cta && <CtaElement cta={cta} />}
        {secondaryCta && <CtaElement cta={secondaryCta} />}
      </div>
    )}
  </div>
);

export default EmptyStateWithRedirect;
